// src/components/CommonComponents/Dialog/Dialog.jsx:
import React from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, HelpCircle, X } from 'lucide-react';
import PropTypes from "prop-types";

// Dialog Types Configuration
const dialogConfig = {
    success: {
        icon: CheckCircle,
        iconColor: 'text-green-500',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        buttonColor: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
        title: 'Success!'
    },
    error: {
        icon: XCircle,
        iconColor: 'text-red-500',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        buttonColor: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
        title: 'Error!'
    },
    warning: {
        icon: AlertTriangle,
        iconColor: 'text-yellow-500',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        buttonColor: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500',
        title: 'Warning!'
    },
    info: {
        icon: Info,
        iconColor: 'text-blue-500',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        buttonColor: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
        title: 'Information'
    },
    confirm: {
        icon: HelpCircle,
        iconColor: 'text-purple-500',
        bgColor: 'bg-purple-50',
        borderColor: 'border-purple-200',
        buttonColor: 'bg-purple-600 hover:bg-purple-700 focus:ring-purple-500',
        title: 'Confirm Action'
    }
};



// Dialog Component
const Dialog = ({ isOpen, onClose, config }) => {
    if (!isOpen) return null;

    const {
        type = 'info',
        title,
        message,
        confirmText = 'OK',
        cancelText = 'Cancel',
        showCancel = false,
        onConfirm,
        onCancel,
        customIcon,
        allowClose = true
    } = config;

    const dialogStyle = dialogConfig[type] || dialogConfig.info;
    const IconComponent = customIcon || dialogStyle.icon;
    const displayTitle = title || dialogStyle.title;

    const handleConfirm = () => {
        if (onConfirm) onConfirm();
        onClose();
    };

    const handleCancel = () => {
        if (onCancel) onCancel();
        onClose();
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && allowClose) {
            onClose();
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm animate-fadeIn"
            onClick={handleBackdropClick}
        >
            <div className="relative w-full max-w-md transform transition-all animate-scaleIn">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                    {/* Close Button */}
                    {allowClose && (
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
                            aria-label="Close dialog"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    )}

                    {/* Icon Section */}
                    <div className={`${dialogStyle.bgColor} ${dialogStyle.borderColor} border-b px-6 py-8 flex justify-center`}>
                        <div className="relative">
                            <div className={`${dialogStyle.iconColor} animate-bounce`}>
                                <IconComponent className="w-16 h-16 sm:w-20 sm:h-20" strokeWidth={1.5} />
                            </div>
                            <div className={`absolute inset-0 ${dialogStyle.iconColor} opacity-20 blur-xl animate-pulse`}>
                                <IconComponent className="w-16 h-16 sm:w-20 sm:h-20" />
                            </div>
                        </div>
                    </div>

                    {/* Content Section */}
                    <div className="px-6 py-6">
                        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-3">
                            {displayTitle}
                        </h3>
                        {message && (
                            <p className="text-sm sm:text-base text-gray-600 text-center leading-relaxed">
                                {message}
                            </p>
                        )}
                    </div>

                    {/* Actions Section */}
                    <div className="px-6 pb-6 flex flex-col-reverse sm:flex-row gap-3">
                        {showCancel && (
                            <button
                                onClick={handleCancel}
                                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
                            >
                                {cancelText}
                            </button>
                        )}
                        <button
                            onClick={handleConfirm}
                            className={`flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${dialogStyle.buttonColor}`}
                        >
                            {confirmText}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

Dialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    config: PropTypes.shape({
        type: PropTypes.string,
        title: PropTypes.string,
        message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
        confirmText: PropTypes.string,
        cancelText: PropTypes.string,
        showCancel: PropTypes.bool,
        onConfirm: PropTypes.func,
        onCancel: PropTypes.func,
        customIcon: PropTypes.elementType,
        allowClose: PropTypes.bool,
    }).isRequired,
};

export default Dialog;